/* eslint-disable @typescript-eslint/no-unused-vars */
import { Heading } from '@abyss/web/ui/Heading';
import { Link } from '@abyss/web/ui/Link';
import { PageBody } from '@abyss/web/ui/PageBody';
import { Text } from '@abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { useGetProviderDirectories } from '../../hooks/useGetProviderDirectoryPdfs/useGetProviderDirectories';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import {
  DocumentLinkContainer,
  ProviderDirectoryContentWrapped,
  ViewDocumentsContainer,
  YearBadge,
  YearContainer,
  providerDirectoryHeadingStyle,
  viewDocumentsTextStyle,
} from './ProviderDirectory.styled';

interface Document {
  name: string;
  language: string;
  year: string;
  link: string;
  docSize: string;
  docLastModifiedDate: string;
}

interface YearGroup {
  year: string;
  documents: Document[];
}

export const ProviderDirectory = () => {
  const planSelectorVariables = getPlanSelectorVariables();
  const { accountId } = planSelectorVariables;
  const [providerDirectoryData, getProviderDirectories] =
    useGetProviderDirectories({
      onCompleted: (data: any) => {
        if (data?.data?.getProviderDirectory) {
          setProviderDirectoryList(data.data?.getProviderDirectory);
        }
      },
    });
  const [providerDirectoryList, setProviderDirectoryList] = useState([]);
  const variables = {
    planContextId: accountId || '',
  };
  useEffect(() => {
    if (accountId) {
      getProviderDirectories({ variables });
    }
  }, [accountId]);

  const { t } = useTranslation();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { locale } = chipStore;
  const [filteredDocs, setFilteredDocs] = useState<YearGroup[]>([]);
  const getActualLocale = (localeStr: string) => {
    switch (localeStr) {
      case 'es':
      case 'es-us':
      case 'es-US':
        return 'es';
      case 'en':
      case 'en-us':
      case 'en-US':
      default:
        return 'en_us';
    }
  };
  useEffect(() => {
    const pdfLocale = getActualLocale(locale);
    const filteredList = providerDirectoryList
      .map((yearGroup: { year: string; documents: Document[] }) => ({
        year: yearGroup.year,
        documents: yearGroup.documents.filter(
          (doc) => doc.language === pdfLocale
        ),
      }))
      .filter((yearGroup) => yearGroup.documents.length > 0);
    setFilteredDocs(filteredList);
  }, [locale, providerDirectoryList]);

  const getPDFName = (document: Document): string => {
    const docSize = document?.docSize || '';
    return (
      t('PROVIDER_DIRECTORY.HEADING') + (docSize ? ` (PDF, ${docSize})` : '')
    );
  };

  return (
    <PageBody
      css={{
        'abyss-page-body-container': {
          backgroundColor: '$pageBodyBgColor',
          maxWidth: '100%',
          padding: 0,
        },
      }}
      id="main-content"
      tabIndex={-1}
    >
      <ProviderDirectoryContentWrapped>
        <Heading
          css={providerDirectoryHeadingStyle}
          data-testid="document-link"
          display="h4"
          offset={1}
        >
          {t('PROVIDER_DIRECTORY.HEADING')}
        </Heading>
        <ViewDocumentsContainer>
          <Text color="#323333" css={viewDocumentsTextStyle}>
            {t('PROVIDER_DIRECTORY.VIEW_DOCUMENTS_IN')}
          </Text>
        </ViewDocumentsContainer>
        {filteredDocs &&
          filteredDocs.length > 0 &&
          filteredDocs.map((yearDocs) => (
            <YearContainer key={yearDocs.year}>
              <YearBadge>{yearDocs.year}</YearBadge>
              {yearDocs.documents &&
                yearDocs.documents.length > 0 &&
                yearDocs.documents.map((document) => (
                  <DocumentLinkContainer key={document.name}>
                    <Link
                      href={document.link}
                      isStandardAnchor={true}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <Text color="$primary" fontWeight={600} size={14}>
                        {getPDFName(document)}
                      </Text>
                    </Link>
                  </DocumentLinkContainer>
                ))}
            </YearContainer>
          ))}
      </ProviderDirectoryContentWrapped>
    </PageBody>
  );
};
